import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import MatchScoreCard from './components/MatchScoreCard';
import SkillGapAnalysis from './components/SkillGapAnalysis';
import JobSnippets from './components/JobSnippets';
import KeywordAnalysis from './components/KeywordAnalysis';
import SkillsSpiderChart from './components/SkillsSpiderChart';
import ActionPanel from './components/ActionPanel';
import ComparisonDashboard from './components/ComparisonDashboard';
import ComparisonService from '../../services/comparisonService';
import ResumeAnalysisService from '../../services/resumeAnalysisService';

const AnalysisResultsDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [analysisData, setAnalysisData] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load analysis data - NO MORE AUTH REDIRECTS NEEDED 
  useEffect(() => {
    if (user && id) {
      loadAnalysisData();
    }
  }, [user, id]);

  const loadAnalysisData = async () => {
    if (!user?.id || !id) return;

    try {
      setLoading(true);
      setError(null);

      // Fix: Handle direct data return (not { data, error } format)
      const analysisResult = await ResumeAnalysisService.getAnalysisById(id);

      if (!analysisResult) {
        setError('Analysis not found');
        return;
      }

      // Security check: Verify this analysis belongs to the current user
      if (analysisResult.user_id !== user.id) {
        setError('You do not have permission to view this analysis');
        return;
      }

      setAnalysisData(analysisResult);

      // Load comparison data if available
      const comparisonData = await ComparisonService.getComparisonForScan(id);
      if (comparisonData) {
        setComparison(comparisonData);
      }

      // If this is a live analysis, redirect to the live results page
      if (analysisResult?.isLiveAnalysis && analysisResult?.liveAnalysisData) {
        navigate('/analysis-results-live', {
          state: {
            analysis: analysisResult.liveAnalysisData.analysis,
            jobPosting: analysisResult.liveAnalysisData.jobPosting
          }
        });
        return;
      }

      // DEBUG: Log loaded data
      console.log('📊 Analysis Data Loaded:', {
        role: analysisResult.role,
        overall_score: analysisResult.overall_score,
        job_matches_count: analysisResult.job_matches?.length || 0,
        job_matches: analysisResult.job_matches
      });

    } catch (err) {
      console.error('Analysis load error:', err);
      setError(err.message || 'Failed to load analysis results');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while data loads - auth loading handled by ProtectedRoute
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading analysis results...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Failed to Load Analysis</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <div className="space-x-4">
            <button
              onClick={loadAnalysisData}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/user-dashboard')}
              className="px-4 py-2 border border-border text-foreground rounded-md hover:bg-muted"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analysisData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Analysis Not Found</h2>
          <p className="text-muted-foreground mb-4">The requested analysis could not be found.</p>
          <button
            onClick={() => navigate('/user-dashboard')}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const {
    role,
    overall_score,
    skills_match,
    missing_skills,
    improvement_suggestions,
    extracted_skills,
    job_matches,
    analysis_summary,
    created_at,
    ats_keywords,
    resume_keywords
  } = analysisData;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="ArrowLeft" size={20} />
                <span>Back to Dashboard</span>
              </button>
              <div className="h-6 w-px bg-border" />
              <div>
                <h1 className="text-lg font-semibold text-foreground">Analysis Results</h1>
                <p className="text-sm text-muted-foreground">{role} · {new Date(created_at)?.toLocaleDateString()}</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Overall Score:</span>
              <div className={`px-2 py-1 rounded-full text-sm font-medium ${overall_score >= 80 ? 'bg-green-100 text-green-700' :
                overall_score >= 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                }`}>
                {overall_score}%
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Comparison Dashboard - Show if this is a re-scan */}
            {comparison && (
              <ComparisonDashboard
                comparison={comparison}
                currentScan={analysisData}
                onViewPreviousScan={() => navigate(`/analysis-results/${comparison.previousScanId}`)}
              />
            )}
            {/* Match Score Overview */}
            <MatchScoreCard
              overallScore={overall_score}
              skillsMatch={skills_match}
              analysisData={analysisData}
              role={role}
            />

            {/* Skills Spider Chart */}
            <SkillsSpiderChart
              extractedSkills={extracted_skills}
              missingSkills={missing_skills}
              role={role}
            />



            {/* Job Matches */}
            <JobSnippets
              jobs={job_matches || []}
              onViewJob={(job) => {
                if (job?.external_url) {
                  window.open(job?.external_url, '_blank');
                }
              }}
            />

            {/* Keyword Analysis */}
            <KeywordAnalysis
              resumeKeywords={resume_keywords || []}
              atsKeywords={ats_keywords || []}
              missingKeywords={[]}
            />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Skill Gap Analysis */}
            <SkillGapAnalysis
              missingSkills={missing_skills}
              skillsMatch={skills_match}
              improvementSuggestions={improvement_suggestions}
            />

            {/* Action Panel */}
            <ActionPanel
              analysisId={id}
              analysisData={analysisData}
              onAction={(action) => {
                switch (action) {
                  case 'rewrite': navigate('/resume-rewriter', {
                    state: {
                      analysisData,
                      suggestions: improvement_suggestions
                    }
                  });
                    break;
                  case 'learning': navigate('/skill-gaps', {
                    state: {
                      missingSkills: missing_skills,
                      role,
                      analysisId: id
                    }
                  });
                    break;
                  case 'chat': navigate('/ai-chat', {
                    state: {
                      context: `Resume analysis for ${role}`,
                      analysisData
                    }
                  });
                    break;
                  case 'download':
                    // Handle download logic
                    break;
                  default:
                    break;
                }
              }}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnalysisResultsDashboard;